import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { colors, s } from "../lib/theme";

const DATE_FORMATS = [
  { value: "dmy", label: "04/07/2026" },
  { value: "dm", label: "04/07" },
  { value: "iso", label: "2026-07-04" },
];
const TIME_FORMATS = [
  { value: "hm_utc", label: "10:02 UTC" },
  { value: "hm_z", label: "1002Z" },
  { value: "hm", label: "10:02" },
];

const FIELDS_PER_CONTACT = [
  { key: "callsign", label: "Indicativo del participante" },
  { key: "activityName", label: "Nombre de la actividad" },
  { key: "band", label: "Banda" },
  { key: "mode", label: "Modo" },
  { key: "date", label: "Fecha (UTC)", hasFormat: "date" },
  { key: "time", label: "Hora (UTC)", hasFormat: "time" },
  { key: "rstSent", label: "RST enviado" },
  { key: "rstReceived", label: "RST recibido" },
  { key: "exchange", label: "Exchange" },
  { key: "points", label: "Puntos de ese QSO" },
];

const FIELDS_SUMMARY = [
  { key: "callsign", label: "Indicativo del participante" },
  { key: "activityName", label: "Nombre de la actividad" },
  { key: "totalContacts", label: "Total de contactos" },
  { key: "points", label: "Puntos totales" },
  { key: "bands", label: "Bandas trabajadas" },
  { key: "modes", label: "Modos trabajadas" },
];

const MIN_BOX = 3; // % mínimo de ancho/alto al dibujar un cuadro

export default function QslTemplateEditor() {
  const { activityId } = useParams();
  const [activity, setActivity] = useState(null);
  const [positions, setPositions] = useState({});
  const [file, setFile] = useState(null);
  const [selectedField, setSelectedField] = useState("");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const imgRef = useRef(null);
  const dragRef = useRef(null); // { type, field, startX%, startY%, origBox, rect }
  const [, forceRender] = useState(0); // para redibujar mientras se arrastra

  const load = () => {
    api.getActivity(activityId).then((a) => {
      setActivity(a);
      setPositions(a.qsl_field_positions || {});
    }).catch((err) => setError(err.message));
  };
  useEffect(() => { load(); }, [activityId]);

  const fields = activity?.qsl_mode === "summary" ? FIELDS_SUMMARY : FIELDS_PER_CONTACT;
  const placedKeys = new Set(Object.keys(positions));
  const availableFields = fields.filter((f) => !placedKeys.has(f.key));

  const uploadTemplate = async () => {
    if (!file) return;
    setError(null);
    try {
      await api.uploadQslTemplate(activityId, file);
      setFile(null);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const clientToPercent = (clientX, clientY, rect) => ({
    x: Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100)),
    y: Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100)),
  });

  // --- Dibujar un cuadro nuevo ---
  const onImageMouseDown = (e) => {
    if (!selectedField) return;
    const rect = imgRef.current.getBoundingClientRect();
    const p = clientToPercent(e.clientX, e.clientY, rect);
    dragRef.current = { type: "draw", field: selectedField, startX: p.x, startY: p.y, rect, box: { x: p.x, y: p.y, width: 0, height: 0 } };
    forceRender((n) => n + 1);
  };

  // --- Mover un cuadro existente ---
  const onBoxMouseDown = (field) => (e) => {
    e.stopPropagation();
    const rect = imgRef.current.getBoundingClientRect();
    const p = clientToPercent(e.clientX, e.clientY, rect);
    const orig = positions[field];
    dragRef.current = { type: "move", field, startX: p.x, startY: p.y, rect, origBox: orig };
  };

  // --- Redimensionar (esquina inferior derecha) ---
  const onResizeMouseDown = (field) => (e) => {
    e.stopPropagation();
    const rect = imgRef.current.getBoundingClientRect();
    const p = clientToPercent(e.clientX, e.clientY, rect);
    const orig = positions[field];
    dragRef.current = { type: "resize", field, startX: p.x, startY: p.y, rect, origBox: orig };
  };

  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const p = clientToPercent(e.clientX, e.clientY, d.rect);

      if (d.type === "draw") {
        const x = Math.min(d.startX, p.x);
        const y = Math.min(d.startY, p.y);
        const width = Math.abs(p.x - d.startX);
        const height = Math.abs(p.y - d.startY);
        d.box = { x, y, width, height };
        forceRender((n) => n + 1);
      } else if (d.type === "move") {
        const dx = p.x - d.startX;
        const dy = p.y - d.startY;
        const maxX = 100 - d.origBox.width;
        const maxY = 100 - d.origBox.height;
        setPositions((prev) => ({
          ...prev,
          [d.field]: {
            ...prev[d.field],
            x: Math.max(0, Math.min(maxX, d.origBox.x + dx)),
            y: Math.max(0, Math.min(maxY, d.origBox.y + dy)),
          },
        }));
      } else if (d.type === "resize") {
        const width = Math.max(MIN_BOX, d.origBox.width + (p.x - d.startX));
        const height = Math.max(MIN_BOX, d.origBox.height + (p.y - d.startY));
        setPositions((prev) => ({ ...prev, [d.field]: { ...prev[d.field], width, height } }));
      }
    };

    const onUp = () => {
      const d = dragRef.current;
      if (!d) return;
      if (d.type === "draw") {
        if (d.box.width >= MIN_BOX && d.box.height >= MIN_BOX) {
          setPositions((prev) => ({
            ...prev,
            [d.field]: { x: d.box.x, y: d.box.y, width: d.box.width, height: d.box.height, color: "#000000", align: "center" },
          }));
          setSelectedField("");
        }
      }
      dragRef.current = null;
      forceRender((n) => n + 1);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const updateFieldStyle = (key, patch) => {
    setPositions({ ...positions, [key]: { ...positions[key], ...patch } });
  };

  const removeField = (key) => {
    const next = { ...positions };
    delete next[key];
    setPositions(next);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.updateActivity(activityId, { qslFieldPositions: positions });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!activity) {
    return <div style={s.page}>{error ? <div style={s.errorBox}>{error}</div> : <p style={{ color: colors.textDim }}>Cargando…</p>}</div>;
  }

  const drawingBox = dragRef.current?.type === "draw" ? dragRef.current.box : null;

  return (
    <div style={{ ...s.page, maxWidth: 1140 }}>
      <Link to="/organizador" style={{ color: colors.textDim, fontSize: 14, fontWeight: 700, textDecoration: "none" }}>← Volver</Link>
      <h1 style={s.h1}>Plantilla QSL — {activity.name}</h1>
      <p style={s.subtitle}>
        Modo: {activity.qsl_mode === "summary" ? "diploma resumen único" : "una QSL por contacto"}.
        Elige un campo y dibuja arrastrando sobre la imagen — el tamaño de letra se ajusta solo al cuadro.
      </p>

      {error && <div style={{ ...s.errorBox, marginTop: 16 }}>{error}</div>}

      {!activity.qsl_template_url ? (
        <div style={{ ...s.card, marginTop: 20, maxWidth: 480 }}>
          <label style={s.label}>SUBE LA IMAGEN DE LA PLANTILLA (JPG O PNG)</label>
          <input type="file" accept="image/jpeg,image/png" onChange={(e) => setFile(e.target.files?.[0] || null)} style={{ fontSize: 13, color: colors.textDim, marginBottom: 14 }} />
          <button onClick={uploadTemplate} disabled={!file} style={{ ...s.btnPrimary, opacity: !file ? 0.5 : 1 }}>Subir plantilla</button>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 20, marginTop: 20 }}>
          <div>
            <div style={{ marginBottom: 10 }}>
              <label style={s.label}>CAMBIAR PLANTILLA</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input type="file" accept="image/jpeg,image/png" onChange={(e) => setFile(e.target.files?.[0] || null)} style={{ fontSize: 12, color: colors.textDim }} />
                {file && <button onClick={uploadTemplate} style={s.btnGhost}>Subir</button>}
              </div>
            </div>

            <div
              style={{ position: "relative", display: "inline-block", border: `1px solid ${colors.line}`, borderRadius: 8, overflow: "hidden", userSelect: "none" }}
              onMouseDown={onImageMouseDown}
            >
              <img ref={imgRef} src={activity.qsl_template_url} alt="Plantilla QSL" draggable={false} style={{ display: "block", maxWidth: "100%", cursor: selectedField ? "crosshair" : "default" }} />

              {Object.entries(positions).map(([key, pos]) => (
                <div
                  key={key}
                  onMouseDown={onBoxMouseDown(key)}
                  style={{
                    position: "absolute", left: `${pos.x}%`, top: `${pos.y}%`, width: `${pos.width}%`, height: `${pos.height}%`,
                    border: "1.5px dashed #f5a623", background: "rgba(245,166,35,0.12)", cursor: "move",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  <span style={{ fontSize: 10, fontWeight: 700, color: "#8a5a00", background: "rgba(255,255,255,0.85)", padding: "1px 4px", borderRadius: 3, pointerEvents: "none", whiteSpace: "nowrap" }}>
                    {fields.find((f) => f.key === key)?.label || key}
                  </span>
                  <div
                    onMouseDown={onResizeMouseDown(key)}
                    style={{ position: "absolute", right: -4, bottom: -4, width: 10, height: 10, background: colors.amber, border: "1px solid #0a0e13", borderRadius: 2, cursor: "nwse-resize" }}
                  />
                </div>
              ))}

              {drawingBox && (
                <div style={{
                  position: "absolute", left: `${drawingBox.x}%`, top: `${drawingBox.y}%`, width: `${drawingBox.width}%`, height: `${drawingBox.height}%`,
                  border: "1.5px dashed #22c55e", background: "rgba(34,197,94,0.15)", pointerEvents: "none",
                }} />
              )}
            </div>
            {selectedField && (
              <p style={{ fontSize: 12.5, color: colors.amber, marginTop: 8 }}>
                Arrastra sobre la imagen para dibujar el cuadro de "{fields.find((f) => f.key === selectedField)?.label}"
              </p>
            )}
          </div>

          <div>
            {availableFields.length > 0 && (
              <div style={{ ...s.card, marginBottom: 14 }}>
                <div style={s.eyebrow}>AÑADIR CAMPO</div>
                <select
                  value={selectedField}
                  onChange={(e) => setSelectedField(e.target.value)}
                  style={{ ...s.input, background: colors.panel }}
                >
                  <option value="">Elige un campo…</option>
                  {availableFields.map((f) => (
                    <option key={f.key} value={f.key} style={{ background: colors.panelRaised, color: colors.text }}>{f.label}</option>
                  ))}
                </select>
              </div>
            )}

            <div style={s.eyebrow}>CAMPOS COLOCADOS</div>
            {Object.keys(positions).length === 0 && (
              <p style={{ fontSize: 12.5, color: colors.textFaint }}>Todavía no has colocado ningún campo.</p>
            )}
            {Object.entries(positions).map(([key, pos]) => {
              const fieldDef = fields.find((f) => f.key === key);
              return (
                <div key={key} style={{ ...s.card, marginBottom: 10, padding: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: colors.text }}>{fieldDef?.label || key}</span>
                    <button
                      onClick={() => {
                        if (window.confirm(`¿Quitar el campo "${fieldDef?.label || key}" de la plantilla?`)) removeField(key);
                      }}
                      style={{ background: "transparent", border: "none", color: "#ffb4b4", cursor: "pointer", fontSize: 11 }}
                    >
                      Quitar
                    </button>
                  </div>

                  <div style={{ display: "flex", gap: 8, marginBottom: fieldDef?.hasFormat ? 8 : 0 }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: 10, color: colors.textFaint }}>Alineación</label>
                      <select value={pos.align || "center"} onChange={(e) => updateFieldStyle(key, { align: e.target.value })} style={{ ...s.input, padding: "5px 8px", background: colors.panel }}>
                        <option value="left" style={{ background: colors.panelRaised }}>Izquierda</option>
                        <option value="center" style={{ background: colors.panelRaised }}>Centro</option>
                        <option value="right" style={{ background: colors.panelRaised }}>Derecha</option>
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: 10, color: colors.textFaint }}>Color</label>
                      <input type="color" value={pos.color} onChange={(e) => updateFieldStyle(key, { color: e.target.value })} style={{ ...s.input, padding: "2px", height: 34 }} />
                    </div>
                  </div>

                  {fieldDef?.hasFormat === "date" && (
                    <div>
                      <label style={{ fontSize: 10, color: colors.textFaint }}>Formato de fecha</label>
                      <select value={pos.format || "dmy"} onChange={(e) => updateFieldStyle(key, { format: e.target.value })} style={{ ...s.input, padding: "5px 8px", background: colors.panel }}>
                        {DATE_FORMATS.map((f) => <option key={f.value} value={f.value} style={{ background: colors.panelRaised }}>{f.label}</option>)}
                      </select>
                    </div>
                  )}
                  {fieldDef?.hasFormat === "time" && (
                    <div>
                      <label style={{ fontSize: 10, color: colors.textFaint }}>Formato de hora</label>
                      <select value={pos.format || "hm_utc"} onChange={(e) => updateFieldStyle(key, { format: e.target.value })} style={{ ...s.input, padding: "5px 8px", background: colors.panel }}>
                        {TIME_FORMATS.map((f) => <option key={f.value} value={f.value} style={{ background: colors.panelRaised }}>{f.label}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              );
            })}

            {saved && <div style={{ color: colors.green, fontSize: 13, marginBottom: 8 }}>✓ Guardado</div>}
            <button onClick={save} disabled={saving} style={{ ...s.btnPrimary, width: "100%", justifyContent: "center", opacity: saving ? 0.6 : 1 }}>
              {saving ? "Guardando…" : "Guardar posiciones"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
